import { CoreConfig } from '@fanduel/cpe-um-bundle/core-config/library';
import { CorePx } from '@fanduel/cpe-um-bundle/core-px/library';
import { Account } from '@fanduel/cpe-um-bundle/account/library';
import { CoreDeeplinks } from '@fanduel/cpe-um-bundle/core-deeplinks/library';
import { ModalPresenter } from '@fanduel/cpe-um-bundle/core-modal-presenter/library';
import { CoreApiIdentities } from '@fanduel/cpe-um-bundle/core-api-identities/library';
import { CoreWebViewPluginRegistry } from '@fanduel/cpe-um-bundle/core-webview/library';
import { ResponsibleGaming } from '@fanduel/cpe-um-bundle/responsible-gaming/library';
import {
  Config,
  Environment,
  Product,
} from '@fanduel/cpe-um-bundle/core-config/contract';
import { CasinoGameWindowLauncher } from '@fanduel/casino-game-window-launcher';
import CasinoMds from '@fanduel/casino-mds';
import {
  getDecodedClientID,
  getBrandUM,
  getEnvironment,
  getCountry,
} from '@/utilities/getters/accountGetters';

import config from '@/config';
import { PLATFORMS, PRODUCTS } from '@/utilities/constants/game';
import { useStore } from '@/store/useStore';
import { getMDSEndpoint } from '@/api/helpers/get-mds-endpoint';
import { isRealityCheckSchedulerSupported } from '@/unified-modules/rg/reality-check-startup';
import { isNativeRealityCheckEnabled } from '@/unified-modules/rg/native-reality-check';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';

let initialized = false;
let initializingPromise: Promise<boolean> | null = null;
let launchCriticalModulesInitialized = false;
let launchCriticalModulesPromise: Promise<void> | null = null;
let accountModuleInitialized = false;
let accountModulePromise: Promise<void> | null = null;
let showHeaderPluginRegistered = false;

const runBestEffort = async (
  name: string,
  step: () => void | Promise<void>,
): Promise<void> => {
  try {
    await step();
  } catch (error) {
    console.error(`[UNIFIED_MODULES]: ${name} failed:`, error);
  }
};

export const __test_resetInitialized = (value: boolean) => {
  initialized = value;
  launchCriticalModulesInitialized = value;
  accountModuleInitialized = value;
  showHeaderPluginRegistered = value;
  if (!value) {
    initializingPromise = null;
    launchCriticalModulesPromise = null;
    accountModulePromise = null;
  }
};
export const __test_resetInitializingPromise = () =>
  (initializingPromise = new Promise((resolve) => resolve(true)));

const getUnifiedModulesConfig = (): Config => {
  const clientAuthToken =
    config.unifiedModules?.clientId ?? getDecodedClientID(config.clientID);
  const { region, brand, product } = useStore.getState().runtime;
  const runtimeProduct = (product || process.env.PRODUCT || '').toLowerCase();
  const runtimeBrand = brand ?? process.env.BRAND;

  const umConfigEnvType = config.unifiedModules?.env?.type;
  const customConfig = config.unifiedModules?.customConfig;
  const environment: Environment =
    umConfigEnvType === 'custom' && customConfig
      ? {
          type: 'custom',
          api: customConfig.api,
          web: customConfig.web.replace(
            'statePlaceholder',
            region?.toLowerCase?.() ?? '',
          ),
        }
      : ({
          type:
            (umConfigEnvType as ReturnType<typeof getEnvironment>) ??
            getEnvironment(),
          country: getCountry(process.env.COUNTRY as string),
        } as Environment);

  const conf: Config = {
    appDomain: {
      product: runtimeProduct,
      region: region?.toLowerCase?.(),
      brand: getBrandUM(runtimeBrand as string),
    },
    clientAuthToken,
    environment,
  } as Config;

  const xsell = PRODUCTS.XSELL;
  if (runtimeProduct === xsell) {
    conf.appDomain = {
      product: Product.Sportsbook,
      region: region?.toLowerCase?.(),
    };
  }

  // Poker is a "New Venture" product: it is not in core-config's validProducts
  // list, so it must be sent with a `theme` key to pass config validation.
  // (Without this, validateAppDomain throws "appDomain contains invalid or no
  // product".)
  if (runtimeProduct === PRODUCTS.POKER) {
    conf.appDomain = {
      ...conf.appDomain,
      product: PRODUCTS.POKER,
      theme: PRODUCTS.POKER,
    };
  }

  return conf;
};

export const initializeLaunchCriticalModules = async (): Promise<void> => {
  if (launchCriticalModulesInitialized) return;

  if (!launchCriticalModulesPromise) {
    launchCriticalModulesPromise = (async () => {
      await CoreConfig.setConfig(getUnifiedModulesConfig());
      await CorePx.initialize();
      launchCriticalModulesInitialized = true;
    })().finally(() => {
      launchCriticalModulesPromise = null;
    });
  }

  await launchCriticalModulesPromise;
};

export const initializeAccountModule = async (): Promise<void> => {
  if (accountModuleInitialized) return;

  if (!accountModulePromise) {
    accountModulePromise = (async () => {
      await initializeLaunchCriticalModules();
      await Account.initialize();
      accountModuleInitialized = true;
    })().finally(() => {
      accountModulePromise = null;
    });
  }

  await accountModulePromise;
};

const registerShowHeaderPlugin = (): void => {
  if (showHeaderPluginRegistered) return;

  try {
    CoreWebViewPluginRegistry.add({
      id: 'show-header',
      onUrlLoaded: (ref) => {
        ref.updateConfig((conf) => ({
          ...conf,
          showHeader: true,
          showFooter: true,
        }));
      },
    });
    showHeaderPluginRegistered = true;
  } catch (error) {
    if (
      (error as { message?: string })?.message?.includes(
        'A plugin with this id is already registered.',
      )
    ) {
      showHeaderPluginRegistered = true;
      return;
    }

    throw error;
  }
};

export const initModules = async () => {
  const platform = useStore.getState().runtime.platform;
  registerShowHeaderPlugin();

  await initializeLaunchCriticalModules();

  await Promise.all([
    CoreDeeplinks.initialize(),
    initializeAccountModule(),
    Promise.resolve(CoreApiIdentities.initialize()),
    Promise.resolve(ModalPresenter.initialize()),
    runBestEffort('CasinoGameWindowLauncher initialize', () =>
      CasinoGameWindowLauncher.initialize(),
    ),
    runBestEffort('CasinoMds initialize', () =>
      CasinoMds.initialize(getMDSEndpoint()),
    ),
  ]);

  // Session validation and subscription are owned by useAccountUm, matching
  // casino-next. Keep Reality Check startup here because it is module-level.
  // Skipped when the native wrapper, the lobby embed, or Xsell (which shows
  // its own RC) already owns RC, otherwise two modals stack (CAS-68924).
  if (
    isRealityCheckSchedulerSupported() &&
    !(
      isNativeRealityCheckEnabled() ||
      isEmbeddedInLobby() ||
      platform === PLATFORMS.XSELL
    )
  ) {
    // Start the periodic Reality Check. The bundle subscribes to the session
    // and presents the RC modal in-game once the region interval elapses,
    // resuming on session refresh and stopping on logout. Best-effort: a
    // failure here must not break unified-modules init.
    await runBestEffort('ResponsibleGaming startRealityCheck', () =>
      ResponsibleGaming.startRealityCheck(),
    );
  }

  initialized = true;
  return initialized;
};

export const initializeUnifiedModules = async (): Promise<boolean> => {
  if (initialized) {
    return initialized;
  }

  if (initializingPromise) {
    await initializingPromise;
    return initialized;
  }

  initializingPromise = (async () => {
    try {
      initialized = await initModules();
      return initialized;
    } catch (error) {
      console.error(
        '[UNIFIED_MODULES]: Error initializing unified modules:',
        error,
      );

      throw error;
    } finally {
      initializingPromise = null;
    }
  })();

  return initializingPromise;
};
